import React from "react";
import { DollarSign, HandCoins, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import { formatBRL } from "./LoanDetailsDialog";

export function addPeriods(iso, freq, k) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (freq === "weekly") {
    return new Date(Date.UTC(y, m - 1, d + 7 * k)).toISOString().slice(0, 10);
  }
  if (freq === "free") return iso;
  const total = m - 1 + k;
  const ny = y + Math.floor(total / 12);
  const nm = (total % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${ny}-${String(nm).padStart(2, "0")}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

// Unpaid installments of a loan with their due dates (null for "free" without date)
export function unpaidInstallments(loan) {
  const n = loan.num_installments || 1;
  const paid = loan.installments_paid || 0;
  const interestPer = loan.installment_value - loan.principal / n;
  const out = [];
  for (let k = paid; k < n; k++) {
    let due = null;
    if (loan.first_payment_date) {
      due = loan.frequency === "free" ? loan.first_payment_date : addPeriods(loan.first_payment_date, loan.frequency, k);
    }
    out.push({ due, amount: loan.installment_value, interest: Math.max(0, interestPer) });
  }
  return out;
}

const inPeriod = (iso, period) => {
  if (period.type === "all") return true;
  if (!iso) return false;
  return period.type === "month" ? iso.startsWith(period.month) : iso.startsWith(period.year);
};

export function interestShare(l) {
  return l.total_receivable > 0 ? Math.max(0, (l.total_receivable - l.principal) / l.total_receivable) : 0;
}

export function computeLoanSummary(loans, period, today) {
  let receivable = 0, profit = 0, overdue = 0, overdueCount = 0, received = 0, principal = 0, profitRealized = 0;
  for (const l of loans) {
    principal += l.principal || 0;
    const share = interestShare(l);
    for (const p of l.payments || []) {
      if (!inPeriod(p.date, period)) continue;
      received += p.amount || 0;
      profitRealized += p.interest_only ? p.amount || 0 : (p.amount || 0) * share;
    }
    if (l.status === "paid") continue;
    for (const inst of unpaidInstallments(l)) {
      if (!inPeriod(inst.due, period)) continue;
      receivable += inst.amount;
      profit += inst.interest;
      if (inst.due && inst.due < today) {
        overdue += inst.amount;
        overdueCount += 1;
      }
    }
  }
  return { receivable, profit, overdue, overdueCount, received, principal, profitRealized };
}

function Card({ label, value, Icon, color, testid, badge, arc }) {
  return (
    <div
      data-testid={testid}
      className="relative overflow-hidden rounded-xl border p-4"
      style={{
        borderColor: `${color}55`,
        background: `linear-gradient(135deg, ${color}2e 0%, ${color}12 45%, #121212 100%)`,
        boxShadow: `inset 0 1px 0 ${color}33`,
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[0.7rem] uppercase tracking-widest text-zinc-400">{label}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        <div className="font-mono-num text-xl font-bold tabular-nums md:text-2xl" style={{ color }}>
          {value}
        </div>
        {badge}
      </div>
      {arc != null && (
        <svg viewBox="0 0 36 20" className="pointer-events-none absolute -bottom-1 right-3 h-8 w-14 opacity-70">
          <path d="M2 18 A16 16 0 0 1 34 18" fill="none" stroke={`${color}33`} strokeWidth="3" />
          <path
            d="M2 18 A16 16 0 0 1 34 18"
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={`${Math.max(0, Math.min(1, arc)) * 50.3} 100`}
          />
        </svg>
      )}
    </div>
  );
}

export default function LoansSummary({ loans, period, today }) {
  const s = computeLoanSummary(loans, period, today);
  const arc = s.receivable + s.received > 0 ? s.received / (s.receivable + s.received) : 0;
  const showPrincipal = period.type === "all";
  return (
    <div className={`grid grid-cols-2 gap-3 md:gap-4 ${showPrincipal ? "md:grid-cols-5" : "md:grid-cols-4"}`}>
      {showPrincipal && (
        <Card label="Emprestado" value={formatBRL(s.principal)} Icon={DollarSign} color="#60A5FA" testid="loans-principal" />
      )}
      <Card label="A receber" value={formatBRL(s.receivable)} Icon={HandCoins} color="#D4FF00" testid="loans-receivable" arc={arc} />
      <Card label="Recebido" value={formatBRL(s.received)} Icon={CheckCircle2} color="#22C55E" testid="loans-paid" />
      <Card
        label="Em aberto"
        value={formatBRL(s.overdue)}
        Icon={Clock}
        color="#F59E0B"
        testid="loans-outstanding"
        badge={
          s.overdueCount > 0 ? (
            <span
              data-testid="loans-overdue-badge"
              className="mb-1 inline-flex shrink-0 items-center gap-1 rounded-full bg-[#F59E0B]/20 px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-[#F59E0B]"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]" />
              {s.overdueCount} atrasad{s.overdueCount > 1 ? "os" : "o"}
            </span>
          ) : null
        }
      />
      <Card
        label="Lucro"
        value={formatBRL(s.profitRealized)}
        Icon={TrendingUp}
        color="#A78BFA"
        testid="loans-profit"
        badge={
          <span
            data-testid="loans-profit-forecast"
            className="mb-1 inline-flex shrink-0 items-center rounded-full bg-[#A78BFA]/15 px-2 py-0.5 text-[0.62rem] font-semibold text-[#A78BFA]"
            title="Juros das parcelas ainda não pagas neste período"
          >
            + {formatBRL(s.profit)} previsto
          </span>
        }
      />
    </div>
  );
}
